import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Star, Quote, Loader2, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ rating = 5, size = 14 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={size}
        className={
          i <= rating
            ? "fill-[var(--lime)] text-[var(--lime)]"
            : "fill-transparent text-[var(--light-border)]"
        }
        strokeWidth={1.5}
      />
    ))}
  </span>
);

/**
 * BrandReviews — server-side fetched, brand-styled reviews.
 *
 * Modes:
 *   - "badge": small inline rating summary (e.g. for HomeHero)
 *   - "wall":  full multi-column grid (for /reviews page)
 */
export const BrandReviews = ({ mode = "wall", limit }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/reviews/external`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="text-sm text-[var(--text-muted)]">
        Reviews are temporarily unavailable.
      </div>
    );
  }

  if (!data) {
    return mode === "badge" ? <BadgeSkeleton /> : <WallSkeleton />;
  }

  if (mode === "badge") {
    return <BadgeView data={data} />;
  }
  return <WallView data={data} limit={limit} />;
};

const BadgeView = ({ data }) => {
  const count = data.total;
  return (
    <div
      className="inline-flex flex-col items-start gap-2.5 rounded-2xl bg-white border border-[var(--light-border)] px-6 py-5 shadow-sm"
      data-testid="brand-reviews-badge"
    >
      <Stars rating={Math.round(data.average_rating)} size={22} />
      <span className="text-base font-semibold text-[var(--charcoal)] leading-tight">
        Rated 5 stars by {count}{" "}
        {count === 1 ? "participant" : "participants"}
      </span>
    </div>
  );
};

const WallView = ({ data, limit }) => {
  // Stable shuffle for this render so the featured card varies each page load
  // but the order doesn't reflow as the user interacts with a card.
  const shuffled = useMemo(() => {
    const arr = [...data.reviews];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [data.reviews]);
  const reviews = limit ? shuffled.slice(0, limit) : shuffled;
  return (
    <div data-testid="brand-reviews-wall">
      {/* Grid — first card is "featured" (dark, larger) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {reviews.map((r, i) => (
          <ReviewCard key={r.id} review={r} featured={i === 0} />
        ))}
      </div>
    </div>
  );
};

const ReviewCard = ({ review, featured = false }) => {
  const [expanded, setExpanded] = useState(false);
  // Featured card has plenty of vertical real estate — always show full text,
  // no truncation, no "Read more".
  const long = !featured && (review.message || "").length > 220;
  const short =
    long && !expanded
      ? review.message.slice(0, 215).trimEnd() + "…"
      : review.message;

  if (featured) {
    return (
      <article className="md:col-span-2 lg:col-span-1 lg:row-span-2 rounded-2xl bg-[var(--dark-bg)] text-white p-7 md:p-8 flex flex-col relative overflow-hidden grain-dark">
        {/* Lime glow orb */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-[var(--lime)]/15 blur-3xl"
        />
        <div className="relative">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[var(--lime)] mb-5">
            <Quote
              size={20}
              strokeWidth={2.5}
              className="text-[var(--dark-bg)]"
            />
          </div>
          <Stars rating={review.rating} size={16} />
        </div>
        <p className="relative font-['Outfit'] font-semibold text-white text-[19px] md:text-[21px] leading-snug my-5 flex-1">
          {review.message}
        </p>
        <div className="relative pt-5 border-t border-white/10">
          <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-1">
            Featured review
          </p>
          <p className="text-base font-bold text-white">
            {review.display_name}
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="group rounded-2xl bg-white border border-[var(--light-border)] p-6 flex flex-col transition-all hover:border-[var(--charcoal)] hover:shadow-[0_24px_50px_-30px_rgba(15,20,34,0.45)] hover:-translate-y-0.5">
      <div className="flex items-center justify-between mb-5">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--lime)] flex-shrink-0">
          <Quote
            size={14}
            strokeWidth={2.5}
            className="text-[var(--dark-bg)]"
          />
        </span>
        <Stars rating={review.rating} size={14} />
      </div>
      <p className="text-[15px] text-[var(--charcoal)] leading-relaxed mb-5 flex-1">
        {short}
      </p>
      {long && (
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[var(--charcoal)] hover:text-[var(--dark-bg)] mb-4 self-start group/btn"
        >
          {expanded ? "Show less" : "Read more"}
          <ChevronDown
            size={12}
            className={`transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      )}
      <div className="pt-4 border-t border-[var(--light-border)]">
        <p className="text-base font-bold text-[var(--charcoal)]">
          {review.display_name}
        </p>
      </div>
    </article>
  );
};

const BadgeSkeleton = () => (
  <div className="inline-flex flex-col items-start gap-2.5 rounded-2xl bg-white border border-[var(--light-border)] px-6 py-5 shadow-sm">
    <Loader2 size={20} className="text-[var(--teal)] animate-spin" />
    <span className="text-base text-[var(--text-muted)]">Loading reviews...</span>
  </div>
);

const WallSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <div
        key={i}
        className="rounded-2xl bg-white border border-[var(--light-border)] p-6 animate-pulse"
        style={{ animationDelay: `${i * 60}ms` }}
      >
        <div className="flex gap-1 mb-4">
          {[0, 1, 2, 3, 4].map((s) => (
            <div key={s} className="w-3 h-3 rounded-sm bg-[var(--lime)]/40" />
          ))}
        </div>
        <div className="h-3 w-full bg-[var(--light-border)] rounded mb-2" />
        <div className="h-3 w-5/6 bg-[var(--light-border)] rounded mb-2" />
        <div className="h-3 w-4/6 bg-[var(--light-border)] rounded mb-6" />
        <div className="h-3 w-1/3 bg-[var(--light-border)] rounded" />
      </div>
    ))}
  </div>
);

export default BrandReviews;
